// @flow
import React from 'react';

import {
  Fab,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@material-ui/core';
import { withTranslation } from 'react-i18next';
import { createStyles, withStyles, withTheme } from '@material-ui/core/styles';
import Recorder from 'opus-recorder';
import encoderPath from 'opus-recorder/dist/encoderWorker.min';

import Worker from './audioWebWorker';
import { getSnack } from '../../utils/snackbar';
import { getSocketIfExist, getSocketId } from '../../utils/getSocket';
import { sendAudioParams, sendMbackendMessage } from '../../utils/ws-v1';
import AvaMic from '../../../assets/icons/mic-ava.png';
import Headset from '../../../assets/images/headphones.png';

import { log } from '../../utils/log';
import { isMobile } from '../../utils';
import { hasScribe, isPremiumCaptions } from '../../utils/status';

// Base64 is padded with '=', but we use those constants to compare with
// begginings of strings, so we need to strip out the trailing '='.
// Ignoring lint warnings since using template strings here would make it worse.
// eslint-disable-next-line prefer-template
const OpusHeadBase64 = new RegExp('^' + btoa('OpusHead').replace('=', ''));
// eslint-disable-next-line prefer-template
const OpusTagsBase64 = new RegExp('^' + btoa('OpusTags').replace('=', ''));

// Both PCM and OPUS codepaths are currently kept in the code, but eventually
// only Opus should be used.
const USE_OPUS = false;

const styles = createStyles(theme => ({
  headsetPopupContent: {
    fontSize: 18,
    textAlign: 'center',
  },
  headsetPopupCTA: {
    backgroundColor: theme.palette.ava.blue,
    color: 'white',
    display: 'flex',
    flexGrow: 1,
    margin: 20,
    marginBottom: 10,
    marginTop: 0,
  },
  headsetPopupImg: {
    display: 'block',
    margin: 'auto',
    marginBottom: 30,
    marginTop: 30,
  },
  headsetPopupPaper: {
    maxWidth: 400,
  },
  headsetPopupTitle: {
    fontSize: 22,
    fontWeight: 600,
    marginBottom: 20,
    textAlign: 'center',
  },
}));

const MAX_NO_AUDIO_RECORDS = 10;
const INTERNAL_AUDIO_VALUE = 'internal';

type Props = {
  availableMics: Array<any>,
  classes: Object,
  conversationEnded: boolean,
  electronCaptionMode: boolean,
  fullScreen: Boolean,
  hasRecorded: boolean,
  inTwilioCalls: boolean,
  micIdSelected: string,
  recording: boolean,
  scribeCreateTranscript: Function,
  setAvailableMics: Function,
  setMicSelected: Function,
  speechLang: string,
  startRecord: Function,
  status: Object,
  stopRecord: Function,
  t: Function,
  theme: Object,
  user: Object,
};

type State = {
  audioParamsSent: boolean,
  currentSocketId: string,
  bufferedAudio: Array<{ audio_chunk: string, timestamp: number }>,
  logSum: number,
  headsetPopupShown: boolean,
  bluetooth: boolean,
  format: 'pcm' | 'opus' | 'webm',
};

class AudioRecorder extends React.Component<Props, State> {
  source: any;

  processor: any;

  audioContext: any;

  destination: any;

  stream: any;

  shouldResetCount: number = 0;

  audioWebWorker: Worker | typeof undefined;

  recordingStart: number = 0;

  opusRecorder: Recorder;

  // MediaRecorder is not a type known to flow.
  mediaRecorder: Object | typeof undefined;

  currentAudioTimestamp: number = 0;

  constructor(props: Props) {
    super(props);
    this.state = {
      audioParamsSent: false,
      currentSocketId: getSocketId(),
      bufferedAudio: [],
      logSum: 0,
      headsetPopupShown: false,
      bluetooth: false,
      format: 'pcm',
    };
    this.updateDeviceList();
    window.AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!navigator || !navigator.mediaDevices) {
      // eslint-disable-next-line no-console
      console.log('Browser incompatibility');
    } else {
      navigator.mediaDevices.ondevicechange = this.updateDeviceList;
    }
  }

  updateDeviceList = () => {
    if (!navigator || !navigator.mediaDevices) {
      // eslint-disable-next-line no-console
      console.log('Browser incompatibility');
    } else {
      navigator.mediaDevices.enumerateDevices().then(this.handleDeviceList);
    }
  };

  handleDeviceList = devices => {
    const {
      micIdSelected,
      setMicSelected,
      setAvailableMics,
      stopRecord,
    } = this.props;
    const currentAvailableMics = [];
    const defaultMic = devices.find(
      d => d.kind === 'audioinput' && d.deviceId === 'default'
    );
    devices
      .filter(d => d.kind === 'audioinput' && d.deviceId !== 'default')
      .forEach(device => {
        currentAvailableMics.push({
          value: device.deviceId,
          label: device.label,
          default: defaultMic
            ? device.groupId === defaultMic.groupId
            : undefined,
        });
      });
    if (window.navigator.platform.toLowerCase().includes('win') &&
        navigator.mediaDevices &&
        navigator.mediaDevices.getDisplayMedia) {
      // Capturing system-wide audio is currently only available on Windows.
      currentAvailableMics.push({
        value: INTERNAL_AUDIO_VALUE,
        // TODO: translate
        label: 'Internal audio',
        default: false,
      })
    }
    if (!currentAvailableMics.length) {
      currentAvailableMics.push({
        value: 'Missing',
        label: 'No mic found',
        default: false,
      });
    }
    setAvailableMics(currentAvailableMics);
    if (
      !micIdSelected ||
      !currentAvailableMics.find(mic => micIdSelected === mic.value)
    ) {
      setMicSelected(
        (
          currentAvailableMics.find(mic => mic.default) ||
          currentAvailableMics[0]
        ).value
      );
    }
    if (
      micIdSelected &&
      currentAvailableMics.length === 0
    ) {
      setMicSelected("");
      stopRecord();
    }
  };

  updateAudioParams() {
    const { audioParamsSent, bluetooth, format } = this.state;
    if (audioParamsSent) {
      this.setState({ audioParamsSent: false });
      this.logMicrophoneButtonDisplay();
    }
    const { availableMics, speechLang, micIdSelected } = this.props;
    let mic = 'BUILTIN';
    for (let i = 0; i < availableMics.length; i += 1) {
      if (
        availableMics[i].value === micIdSelected &&
        availableMics[i].label.toLowerCase().includes('bluetooth')
      ) {
        mic = 'BT';
      }
    }
    if ((mic === 'BT' && !bluetooth) || (mic !== 'BT' && bluetooth)) {
      this.setState({ bluetooth: mic === 'BT' });
    }
    const socket = getSocketIfExist();
    if (socket && this.audioContext) {
      sendAudioParams(socket, {
        lang: speechLang,
        pFilter: 2,
        mic,
        format,
        sampleRateHz: 16000,
        recordingMode: 'web',
        chunkLengthMs: 60,
        recordingStart: this.recordingStart,
      });
      this.setState({ audioParamsSent: true });
      this.logMicrophoneButtonDisplay();
    }
  }

  componentDidMount() {
    const { recording } = this.props;
    if (recording) {
      this.record();
    }
  }

  componentWillUnmount() {
    this.audioStopRecord();
  }

  componentDidUpdate(prevProps: Props) {
    const {
      conversationEnded,
      speechLang,
      hasRecorded,
      micIdSelected,
      recording,
      stopRecord,
    } = this.props;
    const { audioParamsSent } = this.state;
    if (conversationEnded && recording) {
      stopRecord();
    }
    if (prevProps.speechLang !== speechLang && hasRecorded && audioParamsSent) {
      this.updateAudioParams();
    }
    if (
      prevProps.micIdSelected !== micIdSelected &&
      hasRecorded &&
      audioParamsSent
    ) {
      this.updateAudioParams();
      this.resetAudioRecording();
    }
    if (prevProps.recording !== recording) {
      if (recording) {
        this.record();
      } else {
        this.audioStopRecord();
      }
    }
  }

  log(data) {
    const { scribeCreateTranscript } = this.props;
    scribeCreateTranscript(undefined, JSON.stringify(data), undefined);
  }

  audioStopRecord() {
    if (this.destination) {
      this.destination.disconnect();
      this.destination = null;
    }
    if (this.opusRecorder) {
      this.opusRecorder.stop();
      this.opusRecorder.close();
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    if (this.processor) {
      this.processor.disconnect();
      this.processor.removeEventListener('audioprocess', this.handlePcmAudio);
      this.processor = null;
    }
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (this.stream) {
      this.stream.getTracks()[0].stop();
    }
    this.logMicrophoneButtonDisplay();
  }

  handlePcmAudio = e => {
    // TODO: clear code that repeats.
    const { audioParamsSent } = this.state;
    if (!audioParamsSent) {
      this.updateAudioParams();
    }
    if (!this.audioWebWorker) {
      this.audioWebWorker = new Worker();
      this.audioWebWorker.onmessage = ({ data }: any) => {
        const { recording } = this.props;
        if (recording) {
          this.resetAudioRecordingIfNeeded(data.volume);
          this.setState({
            logSum: Math.floor(10 * Math.log(data.volume)),
          });
        }
        const socket = getSocketIfExist();
        const { currentSocketId, bufferedAudio } = this.state;
        if (!socket || getSocketId() !== currentSocketId) {
          this.setState({
            currentSocketId: getSocketId(),
            audioParamsSent: false,
          });
          if (socket) {
            this.logMicrophoneButtonDisplay();
          }
          bufferedAudio.push(data.audio);
          if (bufferedAudio.length > 100) {
            bufferedAudio.pop();
          }
        } else {
          while (bufferedAudio.length !== 0) {
            sendMbackendMessage(socket, {
              type: 'write',
              ...bufferedAudio.pop(),
            });
          }
          sendMbackendMessage(socket, {
            ...data.audio,
            type: 'write',
          });
        }
      };
      this.audioWebWorker.postMessage({
        type: 'init',
        config: {
          sampleRateHz: e.inputBuffer.sampleRate,
          chunkLengthMs: 60,
          startTimestamp: Date.now(),
        },
      });
    } else {
      this.audioWebWorker.postMessage({
        type: 'audio',
        playbackTimeMs: 1000 * e.playbackTime,
        chunk: e.inputBuffer.getChannelData(0),
      });
    }
  };

  handleWebmAudio = async (e) => {
    const { audioParamsSent, currentSocketId, bufferedAudio } = this.state;
    const { recording, stopRecord } = this.props;
    if (!recording) {
      this.setState({
        bufferedAudio: [],
      });
      return
    }
    if (!audioParamsSent) {
      this.updateAudioParams();
    }
    const currentData = new Uint8Array(await e.data.arrayBuffer());
    if (!currentData.length) return;
    const makeAudioToSend = d => {
      const audioToSend = {
        audio_chunk: btoa(d.reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ''
        )),
        timestamp: this.currentAudioTimestamp,
        audio_timestamp: Date.now(),
      };
      this.currentAudioTimestamp += 60;
      return audioToSend;
    };

    const audioToSend = makeAudioToSend(currentData);

    const socket = getSocketIfExist();
    if (!socket || getSocketId() !== currentSocketId) {
      this.setState({
        currentSocketId: getSocketId(),
        audioParamsSent: false,
      });
      if (socket) {
        this.logMicrophoneButtonDisplay();
      }
      bufferedAudio.push(audioToSend);
      if (bufferedAudio.length > 100) {
        // If 6s pass we stop recording, because webm is stateful, so we can't
        // hold the packets in the buffer forever.
        bufferedAudio.length = 0;
        stopRecord();
      }
    } else {
      const send = (data) => {
        sendMbackendMessage(socket, {
          ...data,
          type: 'write',
        });
      };
      while (bufferedAudio.length !== 0) {
        send(bufferedAudio.shift());
      }
      send(audioToSend);
    }
  };

  handleOpusAudio = arrayBuffer => {
    // TODO: this function potentially performs intense calculations. Would
    // be good to have a worker to do them.
    const { audioParamsSent, currentSocketId, bufferedAudio } = this.state;
    const { recording, stopRecord } = this.props;
    if (!audioParamsSent) {
      this.updateAudioParams();
    }
    if (recording) {
      /** TODO: figure out volume for Opus
       this.resetAudioRecordingIfNeeded(data.volume);
       this.setState({
        logSum: Math.floor(10 * Math.log(data.volume)),
      }); */
    }
    // encodedSample position is always in 48 kHz, and we want the TS in ms
    const currentTimestamp = this.opusRecorder.encodedSamplePosition / 48;
    const audioToSend = {
      // The magic 28: opus-recorder provides arrayBuffers that are real OGG
      // pages (see https://en.wikipedia.org/wiki/Ogg_page). Ava backend expects
      // raw Opus data. The good thing is that the Ogg Page is just header+data.
      // The header is 28 bytes long.
      audio_chunk: btoa(arrayBuffer.slice(28).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ''
      )),
      timestamp: this.recordingStart + currentTimestamp,
      audio_timestamp: Date.now(),
    };
    const socket = getSocketIfExist();
    if (!socket || getSocketId() !== currentSocketId) {
      this.setState({
        currentSocketId: getSocketId(),
        audioParamsSent: false,
      });
      if (socket) {
        this.logMicrophoneButtonDisplay();
      }
      bufferedAudio.push(audioToSend);
      if (bufferedAudio.length > 100) {
        // If 6s pass we stop recording, because opus is stateful, so we can't
        // hold the packets in the buffer forever.
        bufferedAudio.length = 0;
        stopRecord();
      }
    } else {
      while (bufferedAudio.length !== 0) {
        AudioRecorder.sendPresumptiveOpusAudio(bufferedAudio.shift(), socket);
      }
      AudioRecorder.sendPresumptiveOpusAudio(audioToSend, socket);
    }
  };

  static sendPresumptiveOpusAudio(data, socket) {
    // Opus Ogg stream includes frames made of data (we want to send those),
    // but it also includes Opus headers and Tags - the backend does not
    // understand those and crashes the whole socket. That's why they are
    // not sent to the backend.
    if (OpusHeadBase64.test(data.audio_chunk) ||
      OpusTagsBase64.test(data.audio_chunk)) {
      return;
    }
    sendMbackendMessage(socket, {
      ...data,
      type: 'write',
    });
  }

  resetAudioRecordingIfNeeded(volume) {
    if (volume === 0) {
      this.shouldResetCount += 1;
      if (this.shouldResetCount === MAX_NO_AUDIO_RECORDS) {
        this.resetAudioRecording();
      }
    } else {
      this.shouldResetCount = 0;
    }
  }

  resetAudioRecording() {
    const { recording } = this.props;
    if (recording) {
      this.audioStopRecord();
      this.shouldResetCount = 0;
      this.record();
    }
  }

  handleHeadsetPopupAcknoledgement() {
    this.setState({ headsetPopupShown: true });
  }

  async record() {
    const { t, stopRecord, micIdSelected, availableMics } = this.props;
    this.logMicrophoneButtonDisplay();

    // 10/29/20: audioContext is destroyed in audioStopRecord when the user stops recording.
    if (this.audioContext) return;
    if (navigator.permissions) {
      try {
        const queryResult = await navigator.permissions.query({ name: 'microphone' });
        if (queryResult.state === 'denied') {
          const isChrome =
            /Chrome/.test(navigator.userAgent) &&
            /Google Inc/.test(navigator.vendor);
          getSnack(this.props)(t(
            isChrome
              ? 'snackbar.request.microphoneAccessDeniedOnChrome'
              : 'snackbar.request.microphoneAccessDenied'
            ), { variant: 'error' }
          );
          stopRecord();
        }
      } catch (err) {
        // Failing the permission check is generally benign, because it will
        // be redone in getUserMedia. The two errors enumerated here are thrown
        // by the browsers if they do not understand the 'microphone'
        // permission check. In that case the check will happen on getUserMedia.
        if (
          err.message !==
          "'name' member of PermissionDescriptor 'microphone' is not a valid value for enumeration PermissionName." &&
          err.message !==
          "'microphone' (value of 'name' member of PermissionDescriptor) is not a valid value for enumeration PermissionName."
        ) {
          // eslint-disable-next-line
          console.error(err);
        }
      }
    }
    if (!navigator.mediaDevices) {
      getSnack(this.props)(t('snackbar.request.invalidMediaDevice'), {
        variant: 'error',
      });
      stopRecord();
      return;
    }
    const audioConstraints = {
      echoCancellation: true,
      noiseSuppression: false,
      autoGainControl: false,
    };
    try {
      let audioStream;
      if (micIdSelected === INTERNAL_AUDIO_VALUE && !window.isElectron) {
        audioStream = await navigator.mediaDevices.getDisplayMedia({
          audio: audioConstraints,
          // Even though we do not need video, 'Audio only requests are not
          // supported' is the error given by Windows.
          video: true,
        });
        if (!audioStream.getAudioTracks().length) {
          throw new Error('no audio tracks included');
        }
      } else if (micIdSelected === INTERNAL_AUDIO_VALUE && window.isElectron) {
        audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            mandatory: {
              chromeMediaSource: 'desktop',
            },
          },
          video: {
            mandatory: {
              chromeMediaSource: 'desktop',
            },
          },
        })
      } else {
        audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            ...audioConstraints,
            deviceId:
              micIdSelected &&
              availableMics.find(mic => mic.value === micIdSelected)
                ? { exact: micIdSelected }
                : 'default',
          },
        });
      }
      this.stream = audioStream;
      this.audioContext = new AudioContext();
      this.source = this.audioContext.createMediaStreamSource(audioStream);

      const mimeType = 'audio/webm;codecs=opus';
      const shouldUseWebm =
        window.MediaRecorder &&
        window.MediaRecorder.isTypeSupported(mimeType);
      if (shouldUseWebm) {
        this.setState({
          audioParamsSent: false,
          format: 'webm',
        });
        const mediaRecorder = new window.MediaRecorder(this.stream, {
          mimeType,
          audioBitsPerSecond: 32768,
        });
        this.recordingStart = Date.now();
        this.currentAudioTimestamp = Date.now();
        mediaRecorder.ondataavailable = this.handleWebmAudio;
        mediaRecorder.start(100);
        this.mediaRecorder = mediaRecorder;
      } else if (USE_OPUS) {
        this.setState({
          format: 'opus',
        });
        this.opusRecorder = new Recorder({
          encoderPath,
          sourceNode: this.source,
          encoderApplication: 2048, // use 2049 for better quality, or 2051 for Restricted Low Delay
          encoderComplexity: 2, // 0 is faster, 10 is slower
          encoderSampleRate: 16000,
          encoderFrameSize: 60,
          maxFramesPerPage: 1,
          originalSampleRateOverride: 16000,
          streamPages: true,
        });
        this.recordingStart = Date.now();
        this.opusRecorder.ondataavailable = this.handleOpusAudio;
        this.opusRecorder.start();
      } else {
        this.setState({
          format: 'pcm',
        });
        this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
        this.source.connect(this.processor);
        this.destination = this.audioContext.createMediaStreamDestination
          ? this.audioContext.createMediaStreamDestination()
          : this.audioContext.destination;
        this.processor.connect(this.destination);
        this.processor.addEventListener('audioprocess', this.handlePcmAudio);
      }
      this.updateDeviceList();
    } catch (e) {
      let message;
      if (e.message.includes('no audio tracks')) {
        message = t('snackbar.request.shareAudioNeeded');
      } else if (e.message.includes('Permission denied')) {
        message = t('snackbar.request.micPermissionDenied')
      } else {
        message = t('snackbar.request.microphoneMissing');
      }
      getSnack(this.props)(message, {
        variant: 'error',
      });
      stopRecord();
      // eslint-disable-next-line no-console
      console.log(e);
    }
  }

  logMicrophoneButtonDisplay() {
    const { recording } = this.props;
    const { audioParamsSent, logSum, currentSocketId } = this.state;
    log('microphoneButtonDisplay', 'info', {
      color: recording && audioParamsSent ? 'blue' : 'grey',
      web: {
        recording,
        audioParamsSent,
        currentSocketId,
        logSum,
      },
    });
  }

  render() {
    const {
      classes,
      fullScreen,
      electronCaptionMode,
      inTwilioCalls,
      recording,
      startRecord,
      status,
      stopRecord,
      t,
      theme,
      user,
    } = this.props;
    const {
      audioParamsSent,
      bluetooth,
      headsetPopupShown,
      logSum
    } = this.state;

    let background = theme.palette.ava.grey;
    let shadowColor;
    if (recording && audioParamsSent) {
      if (hasScribe(status)) {
        background = theme.palette.ava.green
      } else if (isPremiumCaptions(status, user)) {
        background = theme.palette.ava.gradientBlue;
        shadowColor = theme.palette.ava.darkBlue;
      } else {
        background = theme.palette.ava.blue
      }
    }
    if (!shadowColor) shadowColor = background;
    if (inTwilioCalls || (fullScreen && (!window.isElectron || !electronCaptionMode))) {
      return <div />;
    }

    return (
      <div
        style={{
          position: window.isElectron && electronCaptionMode ? 'fixed' : 'absolute',
          top: window.isElectron && electronCaptionMode ? '50%' : 80,
          right: window.isElectron && electronCaptionMode ? '100%' : 15,
          transform: window.isElectron && electronCaptionMode ? 'translate(calc(100% + 20px), -50%)' : '',
          zIndex: 100,
          WebkitAppRegion: 'no-drag'
        }}
      >
        <Fab
          color="secondary"
          style={{
            boxShadow: recording
              ? `0px 0px 5px ${Math.max(
                logSum / 12 - 5,
                3
              )}px ${shadowColor}80`
              : 'black 0px 0px 0px 0px',
            background,
            display: 'flex',
            flexDirection: 'column',
            color: 'white',
          }}
          onClick={() => (recording ? stopRecord() : startRecord())}
        >
          <img src={AvaMic} alt="ava_mic" height="30" />
        </Fab>
        {!window.isElectron && !electronCaptionMode && (
          <div style={{
            background,
            color: 'white',
            borderRadius: 10,
            textAlign: 'center',
            margin: 'auto',
            paddingLeft: 5,
            paddingRight: 5,
            width: 'fit-content',
            marginTop: -1,
          }}>
            {recording && audioParamsSent ? 'ON' : 'OFF'}
          </div>
        )}
        {/* The Headset Popup will show when the number of users in a convo is at least *3* */}
        <Dialog
          open={
            !headsetPopupShown &&
            !window.isElectron &&
            !electronCaptionMode &&
            recording &&
            audioParamsSent &&
            !isMobile() &&
            !bluetooth &&
            (status.audioStreams || []).filter(as => as.avaId !== user.avaId)
              .length > 1 &&
            status.conversationMode === 'public'
          }
          classes={{ paper: classes.headsetPopupPaper }}
        >
          <DialogContent>
            <img
              src={Headset}
              alt="headphones"
              className={classes.headsetPopupImg}
            />
            <Typography className={classes.headsetPopupTitle}>
              {t('conversation.headsetPopUp.title')}
            </Typography>
            <Typography className={classes.headsetPopupContent}>
              {t('conversation.headsetPopUp.content1')}
            </Typography>
            <Typography className={classes.headsetPopupContent}>
              {t('conversation.headsetPopUp.content2')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.handleHeadsetPopupAcknoledgement()}
              className={classes.headsetPopupCTA}
              autoFocus
            >
              {t('conversation.headsetPopUp.cta')}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}
export default withStyles(styles)(
  withTranslation()(withTheme(AudioRecorder))
);
